import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  // AlertsManagerDevicesQuery,
  AlertsManagerAlertsQuery,
  AlertsManagerAlertUpdateSubscription,
  Maybe,
  Panel,
  useAlertsManagerAlertsQuery,
  // useAlertsManagerDevicesQuery,
  useAlertsManagerAlertUpdateSubscription,
  // useAlertsManagerDeviceUpdateSubscription,
  Zone,
} from '../../types/generated-types';

import { Log, LogCategory } from './services/logger';

import { OnDataOptions } from '@apollo/client/react/types/types';
import { ApolloError } from '@apollo/client';

export type ActiveAlert = NonNullable<
  NonNullable<AlertsManagerAlertsQuery>
>['alerts'][number];

// export type ActiveDevice = NonNullable<
//   NonNullable<AlertsManagerDevicesQuery>
// >['devices'][number];

export type AlertsMap = Record<string, ActiveAlert>;
export type EntityAlertsMap = Record<string, AlertsMap>;
export type ActiveAlertMap = {
  alert: AlertsMap;
  property: EntityAlertsMap;
  source: EntityAlertsMap;
  unit: EntityAlertsMap;
  zone: EntityAlertsMap;
  panel: EntityAlertsMap;
  device: EntityAlertsMap;
};

// In the UI, historically called these minor alerts "notices" and the major alerts "alerts"
// kevin fowler proposed categorizing alerts into critical, major, minor and info categories.
// These two are still mapped in the UI two notices and alerts, for now.
// export const MinorAlertTypes = [
//   'LOW_RH',
//   'HIGH_RH',
//   'LOW_TEMP',
//   'HIGH_TEMP',
//   'LOW_SUPPLY_TEMP',
//   'HIGH_SUPPLY_TEMP',
//   'LOW_RETURN_TEMP',
//   'HIGH_RETURN_TEMP',
//   'LEAK_DETECTED',
// ];

export const MajorAlertTypes = ['DEVICE_OFFLINE', 'EQUIPMENT_ALARM'];

export interface IAlertsSummary {
  majorAlerts: number;
  minorAlerts: number;
}

export type PropertyAlertSummaryMap = Record<string, IAlertsSummary>;

const unknownAlertSummary: IAlertsSummary = {
  majorAlerts: -1,
  minorAlerts: -1,
};

const emptyAlertSummary: IAlertsSummary = {
  majorAlerts: 0,
  minorAlerts: 0,
};

function alertIsMajor(alert: ActiveAlert): boolean {
  return !!alert.type?.id && MajorAlertTypes.includes(alert.type.id);
}

function alertSummariesAreEqual(
  first: IAlertsSummary,
  second: IAlertsSummary,
): boolean {
  return (
    first.majorAlerts === second.majorAlerts &&
    first.minorAlerts === second.minorAlerts
  );
}

export function buildPropertyAlertSummaries(
  alerts: ActiveAlert[],
): PropertyAlertSummaryMap {
  return alerts.reduce<PropertyAlertSummaryMap>((summaries, alert) => {
    const propertyId = alert.property?.id;
    if (!propertyId) {
      return summaries;
    }

    const summary = (summaries[propertyId] ||= {
      majorAlerts: 0,
      minorAlerts: 0,
    });

    if (alertIsMajor(alert)) {
      summary.majorAlerts += 1;
    } else {
      summary.minorAlerts += 1;
    }

    return summaries;
  }, {});
}

export interface PropertyAlertSummaryStore {
  getSnapshot: (propertyId?: string) => IAlertsSummary;
  replaceSummaries: (nextSummaries: PropertyAlertSummaryMap) => void;
  subscribe: (propertyId: string, listener: () => void) => () => void;
}

export function createPropertyAlertSummaryStore(): PropertyAlertSummaryStore {
  let summaries: PropertyAlertSummaryMap = {};
  const listenersByPropertyId = new Map<string, Set<() => void>>();

  function getSnapshot(propertyId?: string): IAlertsSummary {
    if (!propertyId?.trim()) {
      return unknownAlertSummary;
    }

    return summaries[propertyId] ?? emptyAlertSummary;
  }

  function notify(propertyId: string) {
    listenersByPropertyId.get(propertyId)?.forEach((listener) => listener());
  }

  return {
    getSnapshot,
    replaceSummaries(nextSummaries: PropertyAlertSummaryMap) {
      const changedPropertyIds = new Set([
        ...Object.keys(summaries),
        ...Object.keys(nextSummaries),
      ]);

      const normalizedNextSummaries: PropertyAlertSummaryMap = {};
      Object.entries(nextSummaries).forEach(([propertyId, nextSummary]) => {
        const previousSummary = summaries[propertyId];
        normalizedNextSummaries[propertyId] =
          previousSummary &&
          alertSummariesAreEqual(previousSummary, nextSummary)
            ? previousSummary
            : nextSummary;
      });

      const previousSummaries = summaries;
      summaries = normalizedNextSummaries;

      changedPropertyIds.forEach((propertyId) => {
        if (
          !alertSummariesAreEqual(
            previousSummaries[propertyId] ?? emptyAlertSummary,
            summaries[propertyId] ?? emptyAlertSummary,
          )
        ) {
          notify(propertyId);
        }
      });
    },
    subscribe(propertyId: string, listener: () => void) {
      const listeners = listenersByPropertyId.get(propertyId) ?? new Set();
      listeners.add(listener);
      listenersByPropertyId.set(propertyId, listeners);

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          listenersByPropertyId.delete(propertyId);
        }
      };
    },
  };
}

export interface IAlertsManager {
  // Alerts is an Alert map by entity type, then entity id then alertId
  alerts: ActiveAlertMap;
  error?: ApolloError | undefined | null;
  loading: boolean;
}

const defaultAlertsMap: ActiveAlertMap = {
  alert: {},
  property: {},
  source: {},
  unit: {},
  zone: {},
  panel: {},
  device: {},
} as ActiveAlertMap;

const defaultValue: IAlertsManager = {
  alerts: defaultAlertsMap,
  loading: false,
};

interface AlertsProviderProps {
  children: React.ReactNode;
}

const AlertsContext = createContext<IAlertsManager>(defaultValue);
const defaultPropertyAlertSummaryStore = createPropertyAlertSummaryStore();
const PropertyAlertSummaryStoreContext =
  createContext<PropertyAlertSummaryStore>(defaultPropertyAlertSummaryStore);
const AlertStatusStoreContext = createContext<AlertStatusStore | undefined>(
  undefined,
);

AlertsContext.displayName = 'AlertsContext';
PropertyAlertSummaryStoreContext.displayName =
  'PropertyAlertSummaryStoreContext';
AlertStatusStoreContext.displayName = 'AlertStatusStoreContext';

export const AlertsProvider: FC<AlertsProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<ActiveAlertMap>(defaultValue.alerts);
  const propertyAlertSummaryStoreRef = useRef<PropertyAlertSummaryStore>(
    createPropertyAlertSummaryStore(),
  );
  const propertyAlertSummaryStore = propertyAlertSummaryStoreRef.current;
  const alertStatusStoreRef = useRef<AlertStatusStore | null>(null);
  if (!alertStatusStoreRef.current) {
    alertStatusStoreRef.current = createAlertStatusStore();
  }
  const alertStatusStore = alertStatusStoreRef.current;

  const {
    data: alertsData,
    loading: alertsLoading,
    error: alertsError,
  } = useAlertsManagerAlertsQuery();

  // const { data: devicesData } = useAlertsManagerDevicesQuery();

  useEffect(() => {
    if (alertsData) {
      const newAlerts: ActiveAlertMap = {
        alert: {},
        property: {},
        source: {},
        unit: {},
        zone: {},
        panel: {},
        device: {},
      };

      alertsData.alerts.forEach((alert) => {
        newAlerts.alert[alert._id] = alert;
        if (alert.property?.id) {
          (newAlerts.property[alert.property.id] ||= {})[alert._id] = alert;
        }

        // if (alert.unit?.id) {
        //   (newAlerts.unit[alert.unit.id] ||= {})[alert._id] = alert;
        // }
        //
        // if (alert.device?.id) {
        //   (newAlerts.device[alert.device.id] ||= {})[alert._id] = alert;
        // }
      });

      setAlerts(newAlerts);
    }
  }, [alertsData]);

  useEffect(() => {
    propertyAlertSummaryStore.replaceSummaries(
      buildPropertyAlertSummaries(Object.values(alerts.alert)),
    );
    alertStatusStore.replaceAlerts(Object.values(alerts.alert));
  }, [alerts, propertyAlertSummaryStore, alertStatusStore]);

  // const alertSubscriptionHandler = useCallback(() => {
  //   return , [setAlerts]);

  // const deviceSubscriptionHandler = useCallback(() => {
  //   return (
  //     options: OnDataOptions<ActiveAlert & { modelChangeOperation: string }>,
  //   ) => {
  //     if (options.data?.data) {
  //       const alertToUpdate: ActiveAlert = options.data
  //         .data as unknown as ActiveAlert;
  //
  //       switch (alertToUpdate.modelChangeOperation) {
  //         case 'INSERT':
  //           setAlerts((prevAlerts) => {
  //             const newAlerts = { ...prevAlerts };
  //
  //             const existingAlert = prevAlerts.alert[alertToUpdate._id];
  //             if (!existingAlert) {
  //               newAlerts.alert[alertToUpdate._id] = alertToUpdate;
  //               if (alertToUpdate.property?.id) {
  //                 (newAlerts.property[alertToUpdate.property.id] ||= {})[
  //                   alertToUpdate._id
  //                 ] = alertToUpdate;
  //               }
  //             } else {
  //               console.log('Attempt to insert already existing alert!');
  //             }
  //             return newAlerts;
  //           });
  //           break;
  //         case 'UPDATE':
  //           setAlerts((prevAlerts) => {
  //             const newAlerts = { ...prevAlerts };
  //             if (newAlerts.alert[alertToUpdate._id]) {
  //               Object.assign(
  //                 newAlerts.alert[alertToUpdate._id],
  //                 alertToUpdate as unknown as [value: any],
  //               );
  //             } else {
  //               newAlerts.alert[alertToUpdate._id] = alertToUpdate;
  //               if (alertToUpdate.property?.id) {
  //                 (newAlerts.property[alertToUpdate.property.id] ||= {})[
  //                   alertToUpdate._id
  //                 ] = alertToUpdate;
  //               }
  //             }
  //             return newAlerts;
  //           });
  //           break;
  //         case 'DELETE':
  //           setAlerts((prevAlerts) => {
  //             const newAlerts = { ...prevAlerts };
  //             delete newAlerts.alert[alertToUpdate._id];
  //             if (alertToUpdate.property?.id) {
  //               delete (newAlerts.property[alertToUpdate.property.id] ||= {})[
  //                 alertToUpdate._id
  //               ];
  //             }
  //             return newAlerts;
  //           });
  //           break;
  //         default:
  //           Log.error(
  //             `Unhandled alert update operation: ${alertToUpdate.modelChangeOperation}`,
  //             alertToUpdate,
  //             LogCategory.DATA,
  //           );
  //       }
  //     } else {
  //       console.log('Detected empty alert update operation');
  //     }
  //   };
  // }, [setAlerts]);

  useAlertsManagerAlertUpdateSubscription({
    fetchPolicy: 'no-cache',
    onData: (options: OnDataOptions<AlertsManagerAlertUpdateSubscription>) => {
      if (options.data?.data) {
        const alertToUpdate: any = options.data.data.alertEvents;

        // Normalize modelChangeOperation to uppercase for consistent comparison
        // This ensures we handle operations regardless of whether they come as
        // "insert"/"INSERT", "update"/"UPDATE", or "delete"/"DELETE"
        const operation = (
          alertToUpdate.modelChangeOperation || ''
        ).toUpperCase();

        Log.silly(
          `Alert subscription: ${operation} for alert ${alertToUpdate._id}`,
          alertToUpdate,
          LogCategory.DATA,
        );

        switch (operation) {
          case 'INSERT':
            setAlerts((prevAlerts) => {
              const newAlerts = { ...prevAlerts };

              const existingAlert = prevAlerts.alert[alertToUpdate._id];
              if (!existingAlert) {
                // Exclude modelChangeOperation as it's not part of the alert data model
                const { modelChangeOperation, ...alertData } = alertToUpdate;
                newAlerts.alert[alertToUpdate._id] = alertData as ActiveAlert;
                if (alertData.property?.id) {
                  (newAlerts.property[alertData.property.id] ||= {})[
                    alertToUpdate._id
                  ] = newAlerts.alert[alertToUpdate._id];
                }
              } else {
                console.log('Attempt to insert already existing alert!');
              }
              return newAlerts;
            });
            break;
          case 'UPDATE':
            setAlerts((prevAlerts) => {
              const newAlerts = { ...prevAlerts };
              if (newAlerts.alert[alertToUpdate._id]) {
                // Create a new alert object instead of mutating the existing one
                // Exclude modelChangeOperation as it's not part of the alert data model
                const { modelChangeOperation, ...alertData } = alertToUpdate;
                newAlerts.alert[alertToUpdate._id] = {
                  ...newAlerts.alert[alertToUpdate._id],
                  ...alertData,
                };
                // Update the property map if needed
                if (alertData.property?.id) {
                  (newAlerts.property[alertData.property.id] ||= {})[
                    alertToUpdate._id
                  ] = newAlerts.alert[alertToUpdate._id];
                }
              } else {
                // Create a new alert object without modelChangeOperation
                const { modelChangeOperation, ...alertData } = alertToUpdate;
                newAlerts.alert[alertToUpdate._id] = alertData as ActiveAlert;
                if (alertData.property?.id) {
                  (newAlerts.property[alertData.property.id] ||= {})[
                    alertToUpdate._id
                  ] = newAlerts.alert[alertToUpdate._id];
                }
              }
              return newAlerts;
            });
            break;
          case 'DELETE':
            setAlerts((prevAlerts) => {
              // Get the existing alert before deleting it, so we have all relationship data
              const existingAlert = prevAlerts.alert[alertToUpdate._id];

              if (!existingAlert) {
                // Alert doesn't exist, no-op - but still return a new reference to ensure React detects the update attempt
                Log.silly(
                  `DELETE: Alert ${alertToUpdate._id} not found in state`,
                  null,
                  LogCategory.DATA,
                );
                return prevAlerts;
              }

              Log.silly(
                `DELETE: Removing alert ${alertToUpdate._id} for device ${existingAlert.device?.id || 'unknown'}`,
                existingAlert,
                LogCategory.DATA,
              );

              // Create a completely new structure to ensure React detects the change
              const newAlerts: ActiveAlertMap = {
                alert: { ...prevAlerts.alert },
                property: { ...prevAlerts.property },
                source: { ...prevAlerts.source },
                unit: { ...prevAlerts.unit },
                zone: { ...prevAlerts.zone },
                panel: { ...prevAlerts.panel },
                device: { ...prevAlerts.device },
              };

              // Delete from main alert map
              delete newAlerts.alert[alertToUpdate._id];

              // Clean up all entity maps using the existing alert data
              if (existingAlert?.property?.id) {
                const propertyId = existingAlert.property.id;
                if (newAlerts.property[propertyId]?.[alertToUpdate._id]) {
                  newAlerts.property[propertyId] = {
                    ...newAlerts.property[propertyId],
                  };
                  delete newAlerts.property[propertyId][alertToUpdate._id];
                  // Clean up empty property map entries
                  if (
                    Object.keys(newAlerts.property[propertyId]).length === 0
                  ) {
                    delete newAlerts.property[propertyId];
                  }
                }
              }

              if (existingAlert?.device?.id) {
                const deviceId = existingAlert.device.id;
                if (newAlerts.device[deviceId]?.[alertToUpdate._id]) {
                  newAlerts.device[deviceId] = {
                    ...newAlerts.device[deviceId],
                  };
                  delete newAlerts.device[deviceId][alertToUpdate._id];
                  if (Object.keys(newAlerts.device[deviceId]).length === 0) {
                    delete newAlerts.device[deviceId];
                  }
                }
              }

              if (existingAlert?.zone?.id) {
                const zoneId = existingAlert.zone.id;
                if (newAlerts.zone[zoneId]?.[alertToUpdate._id]) {
                  newAlerts.zone[zoneId] = { ...newAlerts.zone[zoneId] };
                  delete newAlerts.zone[zoneId][alertToUpdate._id];
                  if (Object.keys(newAlerts.zone[zoneId]).length === 0) {
                    delete newAlerts.zone[zoneId];
                  }
                }
              }

              if (existingAlert?.panel?.id) {
                const panelId = existingAlert.panel.id;
                if (newAlerts.panel[panelId]?.[alertToUpdate._id]) {
                  newAlerts.panel[panelId] = { ...newAlerts.panel[panelId] };
                  delete newAlerts.panel[panelId][alertToUpdate._id];
                  if (Object.keys(newAlerts.panel[panelId]).length === 0) {
                    delete newAlerts.panel[panelId];
                  }
                }
              }

              if (existingAlert?.unit?.id) {
                const unitId = existingAlert.unit.id;
                if (newAlerts.unit[unitId]?.[alertToUpdate._id]) {
                  newAlerts.unit[unitId] = { ...newAlerts.unit[unitId] };
                  delete newAlerts.unit[unitId][alertToUpdate._id];
                  if (Object.keys(newAlerts.unit[unitId]).length === 0) {
                    delete newAlerts.unit[unitId];
                  }
                }
              }

              if (existingAlert?.sourceId) {
                const sourceId = existingAlert.sourceId;
                if (newAlerts.source[sourceId]?.[alertToUpdate._id]) {
                  newAlerts.source[sourceId] = {
                    ...newAlerts.source[sourceId],
                  };
                  delete newAlerts.source[sourceId][alertToUpdate._id];
                  if (Object.keys(newAlerts.source[sourceId]).length === 0) {
                    delete newAlerts.source[sourceId];
                  }
                }
              }

              Log.silly(
                `DELETE: Alert ${alertToUpdate._id} removed. Remaining alerts: ${Object.keys(newAlerts.alert).length}`,
                null,
                LogCategory.DATA,
              );

              return newAlerts;
            });
            break;
          default:
            Log.error(
              `Unhandled alert update operation: ${operation} (original: ${alertToUpdate.modelChangeOperation})`,
              alertToUpdate,
              LogCategory.DATA,
            );
        }
      } else {
        Log.silly(
          'Alert subscription: Empty data received',
          options.data,
          LogCategory.DATA,
        );
      }
    },
  });

  // useAlertsManagerDeviceUpdateSubscription({
  //   fetchPolicy: 'no-cache',
  //   onData: deviceSubscriptionHandler,
  // });

  const value: IAlertsManager = useMemo(
    () => ({
      alerts: alerts,
      loading: alertsLoading,
      error: alertsError,
    }),
    [alerts, alertsLoading, alertsError],
  );

  Log.silly('rendering alerts manager', null, LogCategory.RENDERING);

  return (
    <PropertyAlertSummaryStoreContext.Provider
      value={propertyAlertSummaryStore}
    >
      <AlertStatusStoreContext.Provider value={alertStatusStore}>
        <AlertsContext.Provider value={value}>
          {children}
        </AlertsContext.Provider>
      </AlertStatusStoreContext.Provider>
    </PropertyAlertSummaryStoreContext.Provider>
  );
};

export function useAlertsManager(): IAlertsManager {
  const context = useContext(AlertsContext);
  if (context === undefined) {
    throw new Error('useAlertsManager must be used within an AlertsProvider');
  }
  return {
    alerts: context.alerts,
    error: context.error,
    loading: context.loading,
  };
}

export function useAllAlerts(): ActiveAlert[] {
  const { alerts } = useAlertsManager();

  return useMemo(() => {
    return Object.values(alerts.alert);
  }, [alerts.alert]);
}

function useAlertStatusStore(): AlertStatusStore {
  const context = useContext(AlertStatusStoreContext);
  if (context === undefined) {
    throw new Error(
      'useAlertStatusStore must be used within an AlertsProvider',
    );
  }

  return context;
}

export interface IPropertyAlerts {
  alerts: ActiveAlert[];
  error?: ApolloError | undefined | null;
  loading: boolean;
}

export interface ISourceAlerts {
  alerts: ActiveAlert[];
  error?: ApolloError | undefined | null;
  loading: boolean;
}

export function usePropertyAlerts(
  propertyId: string | Maybe<string> | undefined | null,
): IPropertyAlerts {
  const { alerts, error, loading } = useAlertsManager();

  return useMemo(() => {
    if (!propertyId) return { alerts: [], loading: false };

    // Get all alerts from the alerts.alert map
    return {
      alerts: Object.values(alerts.alert).filter(
        (alert) => alert.property?.id === propertyId,
      ),
      loading,
      error,
    };
  }, [alerts, loading, error, propertyId]);
}

export function useSourceAlerts(
  sourceId: string | Maybe<string> | undefined | null,
): ISourceAlerts {
  const { alerts, error, loading } = useAlertsManager();

  return useMemo(() => {
    if (!sourceId) return { alerts: [], loading: false };

    // Get all alerts from the alerts.alert map
    return {
      alerts: Object.values(alerts.alert).filter(
        (alert) => alert.sourceId === sourceId,
      ),
      loading,
      error,
    };
  }, [alerts, loading, error, sourceId]);
}

// export function useUnitAlerts(
//   unitId: string | Maybe<string> | undefined,
// ): ActiveAlert[] {
//   const { alerts } = useAlertsManager();
//
//   return useMemo(() => {
//     if (!unitId) return [];
//
//     return Object.values(alerts.alert).filter(
//       (alert) => alert.unit?.id === unitId,
//     );
//   }, [alerts.alert, unitId]);
// }

export function useDeviceAlerts<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.alerts : [];
}

function useDeviceAlertStatus<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D): DeviceAlertStatusSnapshot {
  const alertStatusStore = useAlertStatusStore();
  const deviceId = normalizeAlertStatusKeyValue(device?.deviceId);
  const sourceId = normalizeAlertStatusKeyValue(device?.sourceId);
  const subscribe = useCallback(
    (listener: () => void) =>
      alertStatusStore.subscribeDeviceAlertStatus(
        { deviceId, sourceId },
        listener,
      ),
    [alertStatusStore, deviceId, sourceId],
  );
  const getSnapshot = useCallback(
    () => alertStatusStore.getDeviceAlertStatusSnapshot({ deviceId, sourceId }),
    [alertStatusStore, deviceId, sourceId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useDeviceHasAlerts<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.hasAlerts : false;
}

export function useDevicesHaveAlerts<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.hasAlerts : false;
}

export function useDeviceIsOnline<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.deviceIsOnline : false;
}

export function useDeviceSourceIsOnline<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.sourceIsOnline : false;
}

export function useDeviceAndSourceAreOnline<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.deviceAndSourceAreOnline : false;
}

export function useDeviceOfflineTimestamp<
  D extends
    | {
        deviceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.deviceId?.trim() ? status.deviceOfflineTimestamp : false;
}

export function useSourceOfflineTimestamp<
  D extends
    | {
        sourceId?: string | Maybe<string> | undefined | null;
      }
    | undefined
    | null,
>(device: D) {
  const status = useDeviceAlertStatus(device);

  return device?.sourceId?.trim() ? status.sourceOfflineTimestamp : false;
}

export function useZoneAlerts(
  zoneId: string | Maybe<string> | undefined | null,
  sourceId: string | Maybe<string> | undefined | null,
) {
  const status = useContainerAlertStatus({
    containerId: zoneId,
    containerType: 'Zone',
    sourceId,
  });

  return zoneId?.trim() ? status.alerts : [];
}

export function usePanelAlerts(
  panelId: string | Maybe<string> | undefined | null,
  sourceId: string | Maybe<string> | undefined | null,
) {
  const status = useContainerAlertStatus({
    containerId: panelId,
    containerType: 'Panel',
    sourceId,
  });

  return panelId?.trim() ? status.alerts : [];
}

// export function usePropertyHasAlerts(
//   propertyId?: string | Maybe<string> | null | undefined,
// ) {
//   const { alerts } = useAlertsManager();
//
//   return useMemo(() => {
//     if (!propertyId?.trim()) return false;
//
//     return Object.values(alerts.alert).some(
//       (alert) => alert.property?.id === propertyId,
//     );
//   }, [alerts.alert, propertyId]);
// }
//
// export function useUnitHasAlerts(
//   unitId?: string | Maybe<string> | null | undefined,
// ) {
//   const { alerts } = useAlertsManager();
//
//   return useMemo(() => {
//     if (!unitId?.trim()) return false;
//
//     return Object.values(alerts.alert).some(
//       (alert) => alert.unit?.id === unitId,
//     );
//   }, [alerts.alert, unitId]);
// }

export function usePropertyAlertSummary(
  propertyId?: string | Maybe<string>,
): IAlertsSummary {
  const propertyAlertSummaryStore = useContext(
    PropertyAlertSummaryStoreContext,
  );
  const normalizedPropertyId = propertyId?.trim() ?? '';
  const subscribe = useCallback(
    (listener: () => void) =>
      propertyAlertSummaryStore.subscribe(normalizedPropertyId, listener),
    [normalizedPropertyId, propertyAlertSummaryStore],
  );
  const getSnapshot = useCallback(
    () => propertyAlertSummaryStore.getSnapshot(normalizedPropertyId),
    [normalizedPropertyId, propertyAlertSummaryStore],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export interface IAlertedEntities {
  major: Record<string, boolean>;
  minor: Record<string, boolean>;
  sources: Record<string, boolean>;
}

export const getDefaultAlertedEntities: () => IAlertedEntities = () => ({
  major: {},
  minor: {},
  sources: {},
});

const emptyAlertedEntitiesSnapshot: IAlertedEntities =
  getDefaultAlertedEntities();

export interface DeviceAlertStatusKey {
  deviceId?: string | Maybe<string> | null;
  sourceId?: string | Maybe<string> | null;
}

export interface ContainerAlertStatusKey {
  containerId?: string | Maybe<string> | null;
  containerType?: 'Panel' | 'Zone' | Maybe<string> | null;
  sourceId?: string | Maybe<string> | null;
}

export interface DeviceAlertStatusSnapshot {
  alerts: ActiveAlert[];
  deviceAndSourceAreOnline: boolean;
  deviceIsOnline: boolean;
  deviceOfflineTimestamp: number | null;
  hasAlerts: boolean;
  sourceIsOnline: boolean;
  sourceOfflineTimestamp: number | null;
}

export interface ContainerAlertStatusSnapshot {
  alerts: ActiveAlert[];
  hasAlerts: boolean;
}

export interface AlertStatusStore {
  getContainerAlertStatusSnapshot: (
    key: ContainerAlertStatusKey,
  ) => ContainerAlertStatusSnapshot;
  getDeviceAlertStatusSnapshot: (
    key: DeviceAlertStatusKey,
  ) => DeviceAlertStatusSnapshot;
  getPropertyAlertedEntitiesSnapshot: (propertyId?: string) => IAlertedEntities;
  replaceAlerts: (alerts: ActiveAlert[]) => void;
  subscribeDeviceAlertStatus: (
    key: DeviceAlertStatusKey,
    listener: () => void,
  ) => () => void;
  subscribeContainerAlertStatus: (
    key: ContainerAlertStatusKey,
    listener: () => void,
  ) => () => void;
  subscribePropertyAlertedEntities: (
    propertyId: string,
    listener: () => void,
  ) => () => void;
}

const emptyDeviceAlertStatusSnapshot: DeviceAlertStatusSnapshot = {
  alerts: [],
  deviceAndSourceAreOnline: false,
  deviceIsOnline: false,
  deviceOfflineTimestamp: null,
  hasAlerts: false,
  sourceIsOnline: false,
  sourceOfflineTimestamp: null,
};

const emptyContainerAlertStatusSnapshot: ContainerAlertStatusSnapshot = {
  alerts: [],
  hasAlerts: false,
};

function normalizeAlertStatusKeyValue(
  value?: string | Maybe<string> | null,
): string {
  return value?.trim() ?? '';
}

function normalizeContainerType(
  containerType?: ContainerAlertStatusKey['containerType'],
): ContainerAlertStatusKey['containerType'] {
  return containerType === 'Panel' || containerType === 'Zone'
    ? containerType
    : '';
}

function deviceAlertStatusKey({
  deviceId,
  sourceId,
}: DeviceAlertStatusKey): string {
  return `${normalizeAlertStatusKeyValue(deviceId)}::${normalizeAlertStatusKeyValue(sourceId)}`;
}

function containerAlertStatusKey({
  containerId,
  containerType,
  sourceId,
}: ContainerAlertStatusKey): string {
  return `${normalizeContainerType(containerType)}::${normalizeAlertStatusKeyValue(containerId)}::${normalizeAlertStatusKeyValue(sourceId)}`;
}

function booleanRecordsAreEqual(
  first: Record<string, boolean>,
  second: Record<string, boolean>,
): boolean {
  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);

  return (
    firstKeys.length === secondKeys.length &&
    firstKeys.every((key) => first[key] === second[key])
  );
}

function alertedEntitiesAreEqual(
  first: IAlertedEntities,
  second: IAlertedEntities,
): boolean {
  return (
    booleanRecordsAreEqual(first.major, second.major) &&
    booleanRecordsAreEqual(first.minor, second.minor) &&
    booleanRecordsAreEqual(first.sources, second.sources)
  );
}

function alertArraysAreEqual(
  first: ActiveAlert[],
  second: ActiveAlert[],
): boolean {
  return (
    first.length === second.length &&
    first.every((alert, index) => {
      const secondAlert = second[index];

      return (
        alert._id === secondAlert._id &&
        alert.updated === secondAlert.updated &&
        alert.initiated === secondAlert.initiated &&
        alert.severity === secondAlert.severity &&
        alert.sourceId === secondAlert.sourceId &&
        alert.type?.id === secondAlert.type?.id &&
        alert.device?.id === secondAlert.device?.id &&
        alert.panel?.id === secondAlert.panel?.id &&
        alert.zone?.id === secondAlert.zone?.id
      );
    })
  );
}

function deviceAlertStatusSnapshotsAreEqual(
  first: DeviceAlertStatusSnapshot,
  second: DeviceAlertStatusSnapshot,
): boolean {
  return (
    first.deviceAndSourceAreOnline === second.deviceAndSourceAreOnline &&
    first.deviceIsOnline === second.deviceIsOnline &&
    first.deviceOfflineTimestamp === second.deviceOfflineTimestamp &&
    first.hasAlerts === second.hasAlerts &&
    first.sourceIsOnline === second.sourceIsOnline &&
    first.sourceOfflineTimestamp === second.sourceOfflineTimestamp &&
    alertArraysAreEqual(first.alerts, second.alerts)
  );
}

function containerAlertStatusSnapshotsAreEqual(
  first: ContainerAlertStatusSnapshot,
  second: ContainerAlertStatusSnapshot,
): boolean {
  return (
    first.hasAlerts === second.hasAlerts &&
    alertArraysAreEqual(first.alerts, second.alerts)
  );
}

function buildAlertedEntitiesForProperty(
  alerts: ActiveAlert[],
): IAlertedEntities {
  return alerts.reduce<IAlertedEntities>((result, alert) => {
    if (alert?.type?.id) {
      if (alert.type.id === 'SOURCE_OFFLINE') {
        if (alert.sourceId) {
          result.sources[alert.sourceId] = true;
        }
      } else if (MajorAlertTypes.includes(alert.type.id)) {
        if (alert.device?.id) {
          result.major[alert.device.id] = true;
        }
      } else if (alert.device?.id) {
        result.minor[alert.device.id] = true;
      }
    }

    return result;
  }, getDefaultAlertedEntities());
}

export function createAlertStatusStore(): AlertStatusStore {
  let alertsByPropertyId: Record<string, ActiveAlert[]> = {};
  let alertsByDeviceId: Record<string, ActiveAlert[]> = {};
  let alertsByPanelId: Record<string, ActiveAlert[]> = {};
  let alertsByZoneId: Record<string, ActiveAlert[]> = {};
  let sourceOfflineAlertsBySourceId: Record<string, ActiveAlert[]> = {};
  const propertySnapshots = new Map<string, IAlertedEntities>();
  const deviceStatusSnapshots = new Map<string, DeviceAlertStatusSnapshot>();
  const containerStatusSnapshots = new Map<
    string,
    ContainerAlertStatusSnapshot
  >();
  const deviceStatusKeys = new Map<string, DeviceAlertStatusKey>();
  const containerStatusKeys = new Map<string, ContainerAlertStatusKey>();
  const propertyListeners = new Map<string, Set<() => void>>();
  const deviceStatusListeners = new Map<string, Set<() => void>>();
  const containerStatusListeners = new Map<string, Set<() => void>>();

  function rebuildIndexes(alerts: ActiveAlert[]) {
    alertsByPropertyId = {};
    alertsByDeviceId = {};
    alertsByPanelId = {};
    alertsByZoneId = {};
    sourceOfflineAlertsBySourceId = {};

    alerts.forEach((alert) => {
      const propertyId = alert.property?.id;
      if (propertyId) {
        (alertsByPropertyId[propertyId] ||= []).push(alert);
      }

      const deviceId = alert.device?.id;
      if (deviceId) {
        (alertsByDeviceId[deviceId] ||= []).push(alert);
      }

      const panelId = alert.panel?.id;
      if (panelId) {
        (alertsByPanelId[panelId] ||= []).push(alert);
      }

      const zoneId = alert.zone?.id;
      if (zoneId) {
        (alertsByZoneId[zoneId] ||= []).push(alert);
      }

      if (alert.sourceId && alert.type?.id === 'SOURCE_OFFLINE') {
        (sourceOfflineAlertsBySourceId[alert.sourceId] ||= []).push(alert);
      }
    });
  }

  function buildDeviceAlertStatusSnapshot({
    deviceId,
    sourceId,
  }: DeviceAlertStatusKey): DeviceAlertStatusSnapshot {
    const normalizedDeviceId = normalizeAlertStatusKeyValue(deviceId);
    const normalizedSourceId = normalizeAlertStatusKeyValue(sourceId);
    const deviceAlerts = normalizedDeviceId
      ? (alertsByDeviceId[normalizedDeviceId] ?? [])
      : [];
    const sourceOfflineAlerts = normalizedSourceId
      ? (sourceOfflineAlertsBySourceId[normalizedSourceId] ?? [])
      : [];
    const alertsById = new Map<string, ActiveAlert>();

    [...deviceAlerts, ...sourceOfflineAlerts].forEach((alert) => {
      alertsById.set(alert._id, alert);
    });

    const deviceOfflineAlert = deviceAlerts.find(
      (alert) => alert.type?.id === 'DEVICE_OFFLINE',
    );
    const sourceOfflineAlert = sourceOfflineAlerts[0];
    const deviceIsOnline = normalizedDeviceId ? !deviceOfflineAlert : false;
    const sourceIsOnline = normalizedSourceId ? !sourceOfflineAlert : true;

    if (!normalizedDeviceId && !normalizedSourceId) {
      return emptyDeviceAlertStatusSnapshot;
    }

    return {
      alerts: Array.from(alertsById.values()),
      deviceAndSourceAreOnline:
        !!normalizedDeviceId && deviceIsOnline && sourceIsOnline,
      deviceIsOnline,
      deviceOfflineTimestamp: deviceOfflineAlert?.initiated ?? null,
      hasAlerts: alertsById.size > 0,
      sourceIsOnline,
      sourceOfflineTimestamp: sourceOfflineAlert?.initiated ?? null,
    };
  }

  function buildContainerAlertStatusSnapshot({
    containerId,
    containerType,
    sourceId,
  }: ContainerAlertStatusKey): ContainerAlertStatusSnapshot {
    const normalizedContainerId = normalizeAlertStatusKeyValue(containerId);
    const normalizedContainerType = normalizeContainerType(containerType);
    const normalizedSourceId = normalizeAlertStatusKeyValue(sourceId);

    if (!normalizedContainerId || !normalizedContainerType) {
      return emptyContainerAlertStatusSnapshot;
    }

    const containerAlerts =
      normalizedContainerType === 'Panel'
        ? (alertsByPanelId[normalizedContainerId] ?? [])
        : (alertsByZoneId[normalizedContainerId] ?? []);
    const sourceOfflineAlerts = normalizedSourceId
      ? (sourceOfflineAlertsBySourceId[normalizedSourceId] ?? [])
      : [];
    const alertsById = new Map<string, ActiveAlert>();

    [...containerAlerts, ...sourceOfflineAlerts].forEach((alert) => {
      alertsById.set(alert._id, alert);
    });

    const alerts = Array.from(alertsById.values());
    return {
      alerts,
      hasAlerts: alerts.length > 0,
    };
  }

  function notifyListeners(listeners: Set<() => void> | undefined) {
    listeners?.forEach((listener) => listener());
  }

  function getPropertyAlertedEntitiesSnapshot(
    propertyId?: string,
  ): IAlertedEntities {
    const normalizedPropertyId = normalizeAlertStatusKeyValue(propertyId);
    if (!normalizedPropertyId) {
      return emptyAlertedEntitiesSnapshot;
    }

    const nextSnapshot = buildAlertedEntitiesForProperty(
      alertsByPropertyId[normalizedPropertyId] ?? [],
    );
    const previousSnapshot = propertySnapshots.get(normalizedPropertyId);

    if (
      previousSnapshot &&
      alertedEntitiesAreEqual(previousSnapshot, nextSnapshot)
    ) {
      return previousSnapshot;
    }

    propertySnapshots.set(normalizedPropertyId, nextSnapshot);
    return nextSnapshot;
  }

  function getDeviceAlertStatusSnapshot(
    key: DeviceAlertStatusKey,
  ): DeviceAlertStatusSnapshot {
    const normalizedKey = {
      deviceId: normalizeAlertStatusKeyValue(key.deviceId),
      sourceId: normalizeAlertStatusKeyValue(key.sourceId),
    };
    const storeKey = deviceAlertStatusKey(normalizedKey);
    deviceStatusKeys.set(storeKey, normalizedKey);

    const nextSnapshot = buildDeviceAlertStatusSnapshot(normalizedKey);
    const previousSnapshot = deviceStatusSnapshots.get(storeKey);

    if (
      previousSnapshot &&
      deviceAlertStatusSnapshotsAreEqual(previousSnapshot, nextSnapshot)
    ) {
      return previousSnapshot;
    }

    deviceStatusSnapshots.set(storeKey, nextSnapshot);
    return nextSnapshot;
  }

  function getContainerAlertStatusSnapshot(
    key: ContainerAlertStatusKey,
  ): ContainerAlertStatusSnapshot {
    const normalizedKey = {
      containerId: normalizeAlertStatusKeyValue(key.containerId),
      containerType: normalizeContainerType(key.containerType),
      sourceId: normalizeAlertStatusKeyValue(key.sourceId),
    };
    const storeKey = containerAlertStatusKey(normalizedKey);
    containerStatusKeys.set(storeKey, normalizedKey);

    const nextSnapshot = buildContainerAlertStatusSnapshot(normalizedKey);
    const previousSnapshot = containerStatusSnapshots.get(storeKey);

    if (
      previousSnapshot &&
      containerAlertStatusSnapshotsAreEqual(previousSnapshot, nextSnapshot)
    ) {
      return previousSnapshot;
    }

    containerStatusSnapshots.set(storeKey, nextSnapshot);
    return nextSnapshot;
  }

  return {
    getContainerAlertStatusSnapshot,
    getDeviceAlertStatusSnapshot,
    getPropertyAlertedEntitiesSnapshot,
    replaceAlerts(alerts: ActiveAlert[]) {
      rebuildIndexes(alerts);

      propertyListeners.forEach((listeners, propertyId) => {
        const previousSnapshot =
          propertySnapshots.get(propertyId) ?? emptyAlertedEntitiesSnapshot;
        const nextSnapshot = buildAlertedEntitiesForProperty(
          alertsByPropertyId[propertyId] ?? [],
        );

        if (!alertedEntitiesAreEqual(previousSnapshot, nextSnapshot)) {
          propertySnapshots.set(propertyId, nextSnapshot);
          notifyListeners(listeners);
        }
      });

      deviceStatusListeners.forEach((listeners, storeKey) => {
        const key = deviceStatusKeys.get(storeKey) ?? {};
        const previousSnapshot =
          deviceStatusSnapshots.get(storeKey) ?? emptyDeviceAlertStatusSnapshot;
        const nextSnapshot = buildDeviceAlertStatusSnapshot(key);

        if (
          !deviceAlertStatusSnapshotsAreEqual(previousSnapshot, nextSnapshot)
        ) {
          deviceStatusSnapshots.set(storeKey, nextSnapshot);
          notifyListeners(listeners);
        }
      });

      containerStatusListeners.forEach((listeners, storeKey) => {
        const key = containerStatusKeys.get(storeKey) ?? {};
        const previousSnapshot =
          containerStatusSnapshots.get(storeKey) ??
          emptyContainerAlertStatusSnapshot;
        const nextSnapshot = buildContainerAlertStatusSnapshot(key);

        if (
          !containerAlertStatusSnapshotsAreEqual(previousSnapshot, nextSnapshot)
        ) {
          containerStatusSnapshots.set(storeKey, nextSnapshot);
          notifyListeners(listeners);
        }
      });
    },
    subscribeContainerAlertStatus(key, listener) {
      const storeKey = containerAlertStatusKey(key);
      const listeners = containerStatusListeners.get(storeKey) ?? new Set();
      containerStatusKeys.set(storeKey, {
        containerId: normalizeAlertStatusKeyValue(key.containerId),
        containerType: normalizeContainerType(key.containerType),
        sourceId: normalizeAlertStatusKeyValue(key.sourceId),
      });
      if (!containerStatusSnapshots.has(storeKey)) {
        containerStatusSnapshots.set(
          storeKey,
          buildContainerAlertStatusSnapshot(key),
        );
      }
      listeners.add(listener);
      containerStatusListeners.set(storeKey, listeners);

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          containerStatusListeners.delete(storeKey);
        }
      };
    },
    subscribeDeviceAlertStatus(key, listener) {
      const storeKey = deviceAlertStatusKey(key);
      const listeners = deviceStatusListeners.get(storeKey) ?? new Set();
      deviceStatusKeys.set(storeKey, {
        deviceId: normalizeAlertStatusKeyValue(key.deviceId),
        sourceId: normalizeAlertStatusKeyValue(key.sourceId),
      });
      if (!deviceStatusSnapshots.has(storeKey)) {
        deviceStatusSnapshots.set(
          storeKey,
          buildDeviceAlertStatusSnapshot(key),
        );
      }
      listeners.add(listener);
      deviceStatusListeners.set(storeKey, listeners);

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          deviceStatusListeners.delete(storeKey);
        }
      };
    },
    subscribePropertyAlertedEntities(propertyId, listener) {
      const normalizedPropertyId = normalizeAlertStatusKeyValue(propertyId);
      const listeners =
        propertyListeners.get(normalizedPropertyId) ?? new Set();
      if (!propertySnapshots.has(normalizedPropertyId)) {
        propertySnapshots.set(
          normalizedPropertyId,
          buildAlertedEntitiesForProperty(
            alertsByPropertyId[normalizedPropertyId] ?? [],
          ),
        );
      }
      listeners.add(listener);
      propertyListeners.set(normalizedPropertyId, listeners);

      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          propertyListeners.delete(normalizedPropertyId);
        }
      };
    },
  };
}

export function useAlertedEntitiesByProperty(
  propertyId?: string | Maybe<string>,
): IAlertedEntities {
  const alertStatusStore = useAlertStatusStore();
  const normalizedPropertyId = propertyId?.trim() ?? '';
  const subscribe = useCallback(
    (listener: () => void) =>
      alertStatusStore.subscribePropertyAlertedEntities(
        normalizedPropertyId,
        listener,
      ),
    [alertStatusStore, normalizedPropertyId],
  );
  const getSnapshot = useCallback(
    () =>
      alertStatusStore.getPropertyAlertedEntitiesSnapshot(normalizedPropertyId),
    [alertStatusStore, normalizedPropertyId],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function useContainerAlertStatus(
  key: ContainerAlertStatusKey,
): ContainerAlertStatusSnapshot {
  const alertStatusStore = useAlertStatusStore();
  const containerId = normalizeAlertStatusKeyValue(key.containerId);
  const containerType = normalizeContainerType(key.containerType);
  const sourceId = normalizeAlertStatusKeyValue(key.sourceId);
  const normalizedKey = useMemo(
    () => ({
      containerId,
      containerType,
      sourceId,
    }),
    [containerId, containerType, sourceId],
  );
  const subscribe = useCallback(
    (listener: () => void) =>
      alertStatusStore.subscribeContainerAlertStatus(normalizedKey, listener),
    [alertStatusStore, normalizedKey],
  );
  const getSnapshot = useCallback(
    () => alertStatusStore.getContainerAlertStatusSnapshot(normalizedKey),
    [alertStatusStore, normalizedKey],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useContainerHasAlerts(
  container:
    | Partial<Zone>
    | Partial<Panel>
    | Maybe<Zone>
    | Maybe<Partial<Zone>>
    | Maybe<Panel>
    | Maybe<Partial<Panel>>
    | undefined,
) {
  const status = useContainerAlertStatus({
    containerId: container?._id,
    containerType: container?.__typename,
    sourceId: container?.sourceId,
  });

  return container?._id?.trim() ? status.hasAlerts : false;
}
